import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import BaseTooltip from "@/components_v2/Tooltip/BaseTooltip";
import BaseMenu from "./BaseMenu";
import { MenuItem, SidebarMenuProps } from "./types";

import "./styles.css";

const COLLAPSE_TRANSITION_MS = 300;
const BADGE_MAX = 99;

const renderLabel = ({ label, badge }: Pick<MenuItem, "label" | "badge">) =>
  badge ? (
    <span className="sidebar-menu-label flex items-center justify-between gap-200">
      {label}

      <span className="sidebar-menu-badge body-s-regular">
        {badge > BADGE_MAX ? `${BADGE_MAX}+` : badge}
      </span>
    </span>
  ) : (
    label
  );

const SidebarMenu = ({
  items,
  isCollapsed = false,
  className,
}: SidebarMenuProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [renderCollapsed, setRenderCollapsed] = useState(isCollapsed);

  useEffect(() => {
    if (isCollapsed) {
      const timer = setTimeout(
        () => setRenderCollapsed(true),
        COLLAPSE_TRANSITION_MS,
      );
      return () => clearTimeout(timer);
    }
    setRenderCollapsed(false);
  }, [isCollapsed]);

  const setSelectedKeysFromCurrentLocation = () => {
    const currentPath = location.pathname;

    let matchingKeys = items
      .filter((item) => currentPath.startsWith(item.key))
      .map((item) => item.key);

    items.forEach((item) => {
      if (item.children) {
        item.children.forEach((child) => {
          if (currentPath.startsWith(child.key)) {
            matchingKeys.push(child.key);
          }
        });
      }
    });

    matchingKeys.sort((a, b) => b.length - a.length);

    if (matchingKeys.length === 0) {
      const pathSegments = currentPath.split("/");
      for (let i = pathSegments.length; i > 0; i--) {
        const parentPath = pathSegments.slice(0, i).join("/") || "/";
        const parentMatch = items.find((item) => item.key === parentPath);
        if (parentMatch) {
          matchingKeys = [parentPath];
          break;
        }
      }
    }

    const path = matchingKeys[0];
    if (path) {
      setSelectedKeys([path]);
    } else {
      setSelectedKeys([]);
    }
  };

  useEffect(() => {
    setSelectedKeysFromCurrentLocation();
  }, [location.pathname, items]);

  const nonNavigableKeys = useMemo(
    () =>
      new Set(
        items.filter((item) => item.disableNavigation).map((item) => item.key),
      ),
    [items],
  );

  const handleMenuItemClick = (itemKey: string) => {
    if (nonNavigableKeys.has(itemKey)) return;
    setSelectedKeys([itemKey]);
    navigate(itemKey);
  };

  const subMenuItems = useMemo(() => {
    return items.filter((item) => item?.children).map((item) => item.key);
  }, [items]);

  const collapsedMenuItems = useMemo(() => {
    return items.flatMap((item) => {
      const parentLeafItem = {
        ...item,
        children: undefined,
      };

      if (!item.children?.length) {
        return [parentLeafItem];
      }

      return [parentLeafItem, ...item.children];
    });
  }, [items]);

  return (
    <BaseMenu
      _internalDisableMenuItemTitleTooltip={renderCollapsed}
      defaultSelectedKeys={selectedKeys}
      defaultOpenKeys={
        renderCollapsed
          ? []
          : items.filter((item) => item?.children).map((item) => item.key)
      }
      openKeys={renderCollapsed ? [] : subMenuItems}
      selectedKeys={selectedKeys}
      className={`sidebar-navigation-menu ${className ?? ""}`}
      mode="inline"
      inlineCollapsed={false}
      onClick={(e) => handleMenuItemClick(e.key)}
      items={(renderCollapsed ? collapsedMenuItems : items).map(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ({ disableNavigation, badge, ...item }) => {
          return {
            ...item,
            children: item.children?.map(
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              ({ badge: childBadge, disableNavigation: _, ...child }) => ({
                ...child,
                label: renderLabel({ label: child.label, badge: childBadge }),
                title: child.label,
              }),
            ),
            icon: renderCollapsed ? (
              <BaseTooltip title={item.label} placement="right">
                {item.icon}
              </BaseTooltip>
            ) : (
              item.icon
            ),
            label: renderLabel({ label: item.label, badge }),
            title: renderCollapsed ? undefined : item.label,
            onTitleClick: renderCollapsed
              ? undefined
              : ({ key, domEvent }) => {
                  domEvent.stopPropagation();
                  handleMenuItemClick(key);
                },
          };
        },
      )}
    />
  );
};

export default SidebarMenu;
